import { networks, getNetworkById, BlockchainNetwork, Stablecoin } from './blockchainConfig';
import { web3Provider } from './web3Provider';

// --- Abstract Blockchain Handler ---
// This defines the common interface that every blockchain-specific handler must implement.
export interface IBlockchainHandler {
  getWalletAddress(): Promise<string>;
  getBalance(stablecoin: Stablecoin): Promise<number>;
  sendTransaction(to: string, amount: number, stablecoin: Stablecoin): Promise<string>; // Returns transaction hash
}


// --- Concrete Handlers (Mocks for now) ---
// In a real app, these would use libraries like ethers.js or solana/web3.js

class EthereumHandler implements IBlockchainHandler {
  private network: BlockchainNetwork;

  constructor(network: BlockchainNetwork) {
    this.network = network;
    console.log(`Initialized handler for ${this.network.name}`);
  }

  async getWalletAddress(): Promise<string> {
    // Mock address
    return '0x1234...EthAddress...5678';
  }

  async getBalance(stablecoin: Stablecoin): Promise<number> {
    console.log(`Fetching ${stablecoin.symbol} balance on ${this.network.name}...`);
    // Mock balance
    return Math.random() * 10000;
  }

  async sendTransaction(to: string, amount: number, stablecoin: Stablecoin): Promise<string> {
    console.log(`Sending ${amount} ${stablecoin.symbol} to ${to} on ${this.network.name}`);
    // Mock transaction hash
    return `0x${[...Array(64)].map(() => Math.floor(Math.random() * 16).toString(16)).join('')}`;
  }
}

class SolanaHandler implements IBlockchainHandler {
    private network: BlockchainNetwork;
  
    constructor(network: BlockchainNetwork) {
      this.network = network;
      console.log(`Initialized handler for ${this.network.name}`);
    }
  
    async getWalletAddress(): Promise<string> {
      return 'So11...SolAddress...1111';
    }
  
    async getBalance(stablecoin: Stablecoin): Promise<number> {
        console.log(`Fetching ${stablecoin.symbol} balance on ${this.network.name}...`);
        return Math.random() * 5000;
    }
  
    async sendTransaction(to: string, amount: number, stablecoin: Stablecoin): Promise<string> {
        console.log(`Sending ${amount} ${stablecoin.symbol} to ${to} on ${this.network.name}`);
        return [...Array(88)].map(() => Math.floor(Math.random() * 16).toString(16)).join('');
    }
  }

// --- The Service (Factory and Facade) ---

// Initialize provider once and reuse
let provider: IBlockchainHandler | null = null;

const getHandler = async (networkId: string): Promise<IBlockchainHandler | null> => {
  // Initialize provider if not already done
  if (!provider) {
    try {
      provider = await web3Provider.initialize();
    } catch (error) {
      console.debug('Error initializing Web3 provider, using mock handler:', error);
      // Continue with mock handler
    }
  }
  const network = getNetworkById(networkId);
  if (!network) {
    console.error(`No network found with ID: ${networkId}`);
    return null;
  }

  switch (network.id) {
    case 'ethereum':
    case 'polygon': // Polygon is EVM-compatible, so it can use the same handler
      return new EthereumHandler(network);
    case 'solana':
      return new SolanaHandler(network);
    default:
      console.error(`No handler implemented for network: ${network.name}`);
      return null;
  }
};

// This is the main service object that the UI will interact with.
// It acts as a facade, hiding the complexity of which handler is being used.
export const blockchainService = {
  getNetworks: () => networks,

  getWalletAddress: async (networkId: string): Promise<string | null> => {
    const handler = await getHandler(networkId);
    return handler ? handler.getWalletAddress() : null;
  },

  getBalance: async (networkId: string, stablecoinSymbol: string): Promise<number | null> => {
    const handler = await getHandler(networkId);
    const network = getNetworkById(networkId);
    const stablecoin = network?.stablecoins.find(s => s.symbol === stablecoinSymbol);

    if (handler && stablecoin) {
      return handler.getBalance(stablecoin);
    }
    return null;
  },

  send: async (networkId: string, to: string, amount: number, stablecoinSymbol: string): Promise<string | null> => {
    const handler = await getHandler(networkId);
    const network = getNetworkById(networkId);
    const stablecoin = network?.stablecoins.find(s => s.symbol === stablecoinSymbol);

    if (handler && stablecoin) {
      return handler.sendTransaction(to, amount, stablecoin);
    }
    return null;
  }
}; 